import moment from 'moment';
import { useCallback, useMemo } from 'react';

import {
    Button,
    Chip,
    TableSortLabel,
    TextField,
    InputAdornment,
    MuiThemeProvider,
    Paper,
    Tooltip,
    Typography,
    withStyles
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import cs from 'classnames';
import DeactivateIcon from '@material-ui/icons/NotInterested';
import ActivateIcon from '@material-ui/icons/PowerSettingsNew';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CustomPagination from 'components/Common/Utils/CustomPagination';
import useDebounce from 'components/TrustCenter/UI/Debounce/Debounce';
import { validateWriteAccess } from 'components/Common/ResourceAccessValidation/ResourceAccessValidation';
import {
    getVisibilityConnections,
    resetAgreementList,
    updateVisibilityConnections,
    setSortOrder,
    setSortField,
    getSharingAgreementV2 as getSharingAgreementV2Action
} from 'components/TrustCenter/store/actions';
import { PRIMARY_BUTTON_LIGHT_THEME } from 'components/UI/Button/ButtonMuiTheme';
import Spinner from 'components/UI/Spinner/Spinner';
import * as Constants from 'constants/Constants';
import Auxiliary from 'hoc/Auxiliary';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { updateSession } from 'store/actions';
import AddConnection from './AddConnection/AddConnection';
import VisibilityConnectionStyles from './VisibilityConnectionStyles';
import Grid from '@material-ui/core/Grid';
import {
    isBlank,
    isEmptyObject,
    isNotEmptyList,
    isNotEmptyObject
} from 'components/Common/Utils/StringUtils';
import {
    SYSTEM_ADMIN,
    CARRIER_ADMIN,
    PAGE,
    PAGE_SIZE,
    SORT_ORDER,
    SORT_FIELD_V1_PROVIDER,
    SORT_FIELD_V2_PROVIDER,
    SHARING_AGREEMENT_STATUS_V2
} from 'constants/Constants';
import {
    MUIDATATABLE_THEME,
    MUIDATATABLE_THEME_ALT,
    SEARCH_BAR_DARK_THEME
} from 'components/TrustCenter/VisibilityConnection/VisibilityConnectionsGridMuiTheme';
import { event } from 'components/Common/Utils/ReactGa4Utils';
import { resolveCarrierConfigType } from 'components/TrustCenter/ProviderDetails/Agreement/CarrierConfig/CarrierConfigPage';
import { listEventTypeDisplayItemsWithGroups } from 'components/TrustCenter/ProviderDetails/SharingAgreementV2/SharingRuleGroupsSection';
import { convertLocalDateTimeString } from 'components/Common/Utils/DateUtils';

/** @typedef {import('../sharingAgreementV2.typedefs.js').V2SharingAgreement} V2SharingAgreement */

/** @param {unknown} raw */
const normalizeGroupRespondedStatus = (raw) => {
    if (!raw || typeof raw !== 'string') {
        return SHARING_AGREEMENT_STATUS_V2.PENDING;
    }
    const lower = raw.trim().toLowerCase();
    if (lower === 'accepted' || lower === 'active') {
        return SHARING_AGREEMENT_STATUS_V2.ACCEPTED;
    }
    if (lower === 'rejected') {
        return SHARING_AGREEMENT_STATUS_V2.REJECTED;
    }
    return SHARING_AGREEMENT_STATUS_V2.PENDING;
};

/** @param {V2SharingAgreement} agreement */
const buildV2StatusSummaryLabel = (agreement) => {
    const counts = { active: 0, pending: 0, rejected: 0 };
    const messageTypes = agreement?.messageTypes;
    let groupCount = 0;
    if (messageTypes && typeof messageTypes === 'object') {
        Object.values(messageTypes).forEach((groups) => {
            if (!Array.isArray(groups)) {
                return;
            }
            groups.forEach((group) => {
                groupCount += 1;
                const n = normalizeGroupRespondedStatus(group?.respondedStatus);
                if (n === SHARING_AGREEMENT_STATUS_V2.ACCEPTED) {
                    counts.active += 1;
                } else if (n === SHARING_AGREEMENT_STATUS_V2.REJECTED) {
                    counts.rejected += 1;
                } else {
                    counts.pending += 1;
                }
            });
        });
    }
    if (groupCount === 0) {
        counts.pending = 1;
    }
    const parts = [];
    if (counts.active) {
        parts.push(`${counts.active} Active`);
    }
    if (counts.pending) {
        parts.push(`${counts.pending} Pending`);
    }
    if (counts.rejected) {
        parts.push(`${counts.rejected} Rejected`);
    }
    return parts.join(', ') || '—';
};

export const VisibilityConnectionsGridBase = (props) => {
    const {
        classes,
        getVisibilityConnections,
        allConnections,
        resetAgreementList,
        updateVisibilityConnections,
        valid,
        disableOtherOperation,
        sidebarResource,
        sessionValue,
        updateSession,
        session,
        account,
        setSortOrder,
        setSortField,
        totalElements,
        totalV2Elements,
        page,
        pageV2,
        size,
        sizeV2,
        sortOrder,
        sortField,
        selectedAccount,
        isTitanServiceEnabled,
        getSharingAgreementV2,
        allSharingAgreementsV2,
        filterProviderAccountId,
        filterProviderDisplayName,
        providerConfigV2,
        history,
        location
    } = props;
    const isV2Surface = location?.pathname?.startsWith('/connections/v2') || !isTitanServiceEnabled;
    const isV1Surface = !isV2Surface;

    const [showModal, setModal] = useState(false);

    const [data, setData] = useState([]);

    const [isWriteAccessGranted, setWriteAccessGranted] = useState(false);

    let editToggle = false;

    const [userRole, setUserRole] = useState({
        isSystemAdmin: false,
        isCarrierAdmin: false,
        isUser: false
    });

    const [isAuthorized, setIsAuthorized] = useState(false);

    const STATUS_COLUMN_INDEX = 2;

    const [showSearchBar, setShowSearchBar] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchTerm = useDebounce(searchValue, 1000);

    const [clientAndPeopleNetIdPresent, setClientAndPeopleNetIdPresent] = useState(false);

    const fetchGridData = (params) => {
        const request = { ...params };
        if (isV2Surface && filterProviderAccountId) {
            request.providerAccountId = filterProviderAccountId;
        }
        isV1Surface ? getVisibilityConnections(request) : getSharingAgreementV2(request);
    };

    const getTotalElements = () => {
        return isV1Surface ? totalElements : totalV2Elements;
    };
    const getPageSize = () => {
        return isV1Surface ? size : sizeV2;
    };
    const getCurrentPage = () => {
        return isV1Surface ? page : pageV2;
    };

    const getSortField = () => {
        return isV1Surface ? SORT_FIELD_V1_PROVIDER : SORT_FIELD_V2_PROVIDER;
    };

    const getDefaultSortOrder = () => {
        return isV1Surface ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    };

    const getColumns = () => {
        return isV1Surface ? columns : columnsSharingAgreementV2;
    };

    const getGridTitle = () => {
        if (isV1Surface) {
            return 'DATA CONNECTIONS';
        }
        if (filterProviderDisplayName) {
            return `SHARING AGREEMENTS — ${filterProviderDisplayName}`;
        }
        return 'SHARING AGREEMENTS';
    };

    useEffect(() => {
        setUserRole({
            ...userRole,
            isSystemAdmin: sessionValue.userRole === SYSTEM_ADMIN,
            isCarrierAdmin: sessionValue.userRole === CARRIER_ADMIN,
            isUser: sessionValue.isUser,
            isVisibilityProvider:
                sessionValue.userRole === Constants.VISIBILITY_PROVIDER ||
                sessionValue.accountType?.includes(Constants.VISIBILITY_PROVIDER)
        });
    }, [sessionValue.userRole]);

    useEffect(() => {
        isNotEmptyObject(account.people_net_id) &&
            updateSession({
                isCompanyIdMapped:
                    account.people_net_id !== null && account.people_net_id.trim() !== ''
            });

        isNotEmptyObject(account.tenant_id) &&
            updateSession({
                isTenantIdMapped: account.tenant_id !== null && account.tenant_id.trim() !== ''
            });

        if (isEmptyObject(account.people_net_id) && isEmptyObject(account.tenant_id))
            setClientAndPeopleNetIdPresent(true);
    }, [account]);

    useEffect(() => {
        clientAndPeopleNetIdPresent &&
            setIsAuthorized(!sessionValue.isCompanyIdMapped && !sessionValue.isTenantIdMapped);
    }, [
        sessionValue.isCompanyIdMapped,
        sessionValue.isTenantIdMapped,
        clientAndPeopleNetIdPresent
    ]);

    useEffect(() => {
        setWriteAccessGranted(
            validateWriteAccess(
                sidebarResource,
                Constants.TRUSTCENTER_RESOURCE,
                Constants.TRUSTCENTER_FEATURE
            )
        );
    }, [sidebarResource]);

    useEffect(() => {
        const defaultSortOrder = getDefaultSortOrder();
        setSortOrder(defaultSortOrder);
        setSortField(getSortField());
        fetchGridData({
            page: PAGE,
            size: getPageSize(),
            sortOrder: defaultSortOrder,
            sortField: getSortField(),
            searchTerm: searchValue
        });
    }, [getVisibilityConnections, getSharingAgreementV2, filterProviderAccountId, isV2Surface]);

    useEffect(() => {
        if (debouncedSearchTerm === '') {
            clearSearch(false);
        } else {
            const debouncedSearchTermTrimmed = debouncedSearchTerm.trim();
            fetchGridData({
                page: PAGE,
                size: PAGE_SIZE,
                sortOrder: sortOrder,
                sortField: sortField,
                searchTerm: debouncedSearchTermTrimmed
            });
        }
    }, [debouncedSearchTerm]);
    const handleSearch = (event) => {
        setSearchValue(event.target.value);
    };
    const clearSearch = (flag) => {
        let params;
        if (flag) {
            setShowSearchBar(false);
            setSearchValue('');
            params = {
                page: PAGE,
                size: PAGE_SIZE,
                sortOrder: getDefaultSortOrder(),
                sortField: getSortField(),
                searchTerm: ''
            };
        } else if (showSearchBar) {
            params = {
                page: getCurrentPage(),
                size: getPageSize(),
                sortOrder: getDefaultSortOrder(),
                sortField: getSortField(),
                searchTerm: ''
            };
        }
        params && fetchGridData(params);
    };

    useEffect(() => {
        if (isV1Surface) {
            setData(formulateDataForGrid(allConnections));
        }
    }, [allConnections, isV1Surface]);

    useEffect(() => {
        if (isV2Surface) {
            setData(formulateDataForGrid(allSharingAgreementsV2));
        }
    }, [allSharingAgreementsV2, isV2Surface]);

    const handleSort = (selectedField) => {
        var order;
        if (sortField !== selectedField) {
            setSortField(selectedField);
            order = SORT_ORDER.ASC;
        } else {
            order = sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
        }
        setSortOrder(order);
        fetchGridData({
            page: getCurrentPage(),
            size: getPageSize(),
            sortOrder: order,
            sortField: selectedField,
            searchTerm: searchValue
        });
    };

    const clickHandle = () => {
        event('Add Connection', 'ClickAddConnectionButton', `${session.accountName}`);
        setModal(true);
    };

    const handleClose = () => {
        setModal(false);
        resetAgreementList();
    };

    const navigateToV2Agreement = useCallback(
        (agreementId) => {
            const isSystemAdmin = sessionValue.userRole === SYSTEM_ADMIN;
            const accountQuery =
                isSystemAdmin && selectedAccount?.id
                    ? `?accountId=${encodeURIComponent(selectedAccount.id)}`
                    : '';
            const v2AgreementBack = {
                pathname: location.pathname,
                search: location.search || '',
                ...(filterProviderAccountId && filterProviderDisplayName
                    ? { providerName: filterProviderDisplayName }
                    : {})
            };
            history.push({
                pathname: `/connections/v2/agreements/${agreementId}`,
                ...(accountQuery ? { search: accountQuery } : {}),
                state: { v2AgreementBack }
            });
        },
        [
            sessionValue.userRole,
            selectedAccount?.id,
            history,
            location.pathname,
            location.search,
            filterProviderAccountId,
            filterProviderDisplayName
        ]
    );

    const agreementForProvider = useMemo(
        () =>
            filterProviderAccountId
                ? allSharingAgreementsV2?.find(
                      (agreement) => agreement?.providerAccount === filterProviderAccountId
                  )
                : undefined,
        [allSharingAgreementsV2, filterProviderAccountId]
    );
    const providerNameFromConfig = useMemo(
        () =>
            filterProviderAccountId
                ? providerConfigV2?.find((p) => p.providerAccountId === filterProviderAccountId)
                      ?.providerName
                : undefined,
        [providerConfigV2, filterProviderAccountId]
    );

    const resolvedProviderName = useMemo(() => {
        return (
            filterProviderDisplayName ||
            agreementForProvider?.providerAccountName ||
            providerNameFromConfig ||
            ''
        );
    }, [agreementForProvider, providerNameFromConfig, filterProviderDisplayName]);
    const carrierConfigType = resolveCarrierConfigType({
        displayName: filterProviderDisplayName,
        configName: providerNameFromConfig,
        agreementName: agreementForProvider?.providerAccountName
    });

    const navigateToCarrierConfig = useCallback(() => {
        const isSystemAdmin = sessionValue.userRole === SYSTEM_ADMIN;
        const accountQuery =
            isSystemAdmin && selectedAccount?.id
                ? `?accountId=${encodeURIComponent(selectedAccount.id)}`
                : location.search || '';
        history.push({
            pathname: `/connections/v2/provider/${filterProviderAccountId}/carrier-config`,
            ...(accountQuery ? { search: accountQuery } : {}),
            state: {
                carrierConfigType,
                providerName: resolvedProviderName
            }
        });
    }, [
        sessionValue.userRole,
        selectedAccount?.id,
        location.search,
        filterProviderAccountId,
        carrierConfigType,
        resolvedProviderName,
        history
    ]);

    const navigateToCreateV2Agreement = useCallback(() => {
        const isSystemAdmin = sessionValue.userRole === SYSTEM_ADMIN;
        const accountQuery =
            isSystemAdmin && selectedAccount?.id
                ? `?accountId=${encodeURIComponent(selectedAccount.id)}`
                : location.search || '';

        const providerAccountName = resolvedProviderName;

        const v2AgreementBack = {
            pathname: location.pathname,
            search: location.search || '',
            ...(filterProviderAccountId && filterProviderDisplayName
                ? { providerName: filterProviderDisplayName }
                : {})
        };
        history.push({
            pathname: `/connections/v2/provider/${filterProviderAccountId}/create`,
            ...(accountQuery ? { search: accountQuery } : {}),
            state: {
                newAgreementProviderAccount: filterProviderAccountId || '',
                newAgreementProviderAccountName: providerAccountName,
                v2AgreementBack
            }
        });
    }, [
        sessionValue.userRole,
        selectedAccount?.id,
        location.pathname,
        location.search,
        filterProviderAccountId,
        filterProviderDisplayName,
        resolvedProviderName,
        history
    ]);

    /**
     * @param {Array|undefined} connectdata V1 connection rows when Titan is enabled; ignored for V2 (uses `allSharingAgreementsV2`).
     * @returns {Array} V2 rows are built from {@link V2SharingAgreement} items.
     */
    const formulateDataForGrid = (connectdata) => {
        let filteredResult = [];
        if (isV1Surface && isNotEmptyObject(connectdata) && connectdata.length > 0) {
            connectdata.forEach((rowData) => {
                const nameId = {
                    id: rowData.id,
                    name: rowData.name
                };
                const category = rowData.type;
                const status = rowData.status;
                const sharing_agreement = rowData.sharing_agreement;
                const connection_type = rowData.connection_types;
                const filteredData = [nameId, category, status, sharing_agreement, connection_type];
                filteredResult.push(filteredData);
            });
        } else if (isV2Surface) {
            const rows = Array.isArray(connectdata) ? connectdata : [];
            rows.forEach((rowData) => {
                const eventTypeItems = listEventTypeDisplayItemsWithGroups(rowData?.messageTypes);
                const direction = rowData?.direction;
                const statusSummary = buildV2StatusSummaryLabel(rowData);
                const createdAt = rowData?.createdAt;
                const validFrom = rowData?.validFrom;
                const validTo = rowData?.validTo;
                const agreementId = rowData?.id;
                const filteredData = [
                    eventTypeItems,
                    direction,
                    statusSummary,
                    { validFrom, validTo },
                    createdAt,
                    { id: agreementId }
                ];
                filteredResult.push(filteredData);
            });
        }
        return filteredResult;
    };

    const getConnectionType = (type) => {
        if (type === Constants.AGREEMENT_TYPE_VISIBILITY) {
            return Constants.AGREEMENT_TYPE_VISIBILITY_UI;
        } else if (type === Constants.ACCOUNT_TYPE_SHIPPER.toUpperCase()) {
            return Constants.ACCOUNT_TYPE_SHIPPER;
        } else if (type === Constants.ACCOUNT_TYPE_VENDOR.toUpperCase()) {
            return Constants.ACCOUNT_TYPE_VENDOR;
        } else {
            return type;
        }
    };

    const sortDirection = useMemo(
        () => (sortOrder === SORT_ORDER.ASC ? 'asc' : 'desc'),
        [sortOrder]
    );

    const columns = [
        {
            name: 'Provider Name',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() =>
                                handleSort(
                                    isV1Surface
                                        ? SORT_FIELD_V1_PROVIDER
                                        : SORT_FIELD_V2_PROVIDER
                                )
                            }
                            key={'Provider Name'}
                        >
                            {'Provider Name'}
                            <TableSortLabel
                                className={classes.inline}
                                active={
                                    sortField ===
                                    (isV1Surface
                                        ? SORT_FIELD_V1_PROVIDER
                                        : SORT_FIELD_V2_PROVIDER)
                                }
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    return (
                        <Tooltip title={'Navigate to Agreements'}>
                            <Typography
                                variant={'subtitle1'}
                                className={classes.providerName}
                                id={'connection_' + value.name}
                                date-connection-id={value.id}
                                key={'Provider Name'}
                            >
                                {value.name}
                            </Typography>
                        </Tooltip>
                    );
                }
            }
        },
        {
            name: 'Category',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => isV1Surface && handleSort('type')}
                            key={'Category'}
                        >
                            {'Category'}
                            {isV1Surface && (
                                <TableSortLabel
                                    className={classes.inline}
                                    active={sortField === 'type'}
                                    direction={sortDirection}
                                />
                            )}
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    return (
                        <Typography id={'category'} key={'Category'}>
                            {' '}
                            {getConnectionType(value)}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'Status',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => isV1Surface && handleSort('status')}
                            key={'Status'}
                        >
                            {'Status'}
                            <TableSortLabel
                                className={classes.inline}
                                active={sortField === 'status'}
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    return (
                        <Typography id={'status'} key={'Status'}>
                            {' '}
                            {value ? 'Active' : 'Inactive'}
                        </Typography>
                    );
                }
            }
        },
        {
            name: '# of Sharing Agreements',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => handleSort('sharing_agreement')}
                            key={'# of Sharing Agreements'}
                        >
                            {'# of Sharing Agreements'}
                            <TableSortLabel
                                className={classes.inline}
                                active={sortField === 'sharing_agreement'}
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    return (
                        <Typography
                            id={'sharing-agreements'}
                            style={{ textAlign: 'left' }}
                            key={'# of Sharing Agreements'}
                        >
                            {' '}
                            {value}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'Connection Type',
            options: {
                sort: false,
                customHeadRender: () => {
                    return (
                        <th className={classes.tableHeader} key={'Connection Type'}>
                            {'Connection Type'}
                            <TableSortLabel className={classes.inline} />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    const connectionTypes = Array.isArray(value) ? value : [];
                    if (!Array.isArray(value) && !isBlank(value)) {
                        connectionTypes.push(value);
                    }
                    return (
                        isNotEmptyList(connectionTypes) &&
                        connectionTypes.map((type) => {
                            if (type === Constants.AGREEMENT_TRUCK) {
                                type = 'GPS';
                            } else if (type === Constants.AGREEMENT_TYPE_CTA) {
                                type = Constants.AGREEMENT_TYPE_CTA_SHORT;
                            }
                            return (
                                <Chip
                                    id={type}
                                    className={classes.chip}
                                    label={type}
                                    key={'Connection Type' + type}
                                />
                            );
                        })
                    );
                }
            }
        }
    ];

    const columnsSharingAgreementV2 = [
        {
            name: 'eventTypes',
            options: {
                sort: false,
                customHeadRender: () => (
                    <th className={classes.tableHeader} key="eventTypes">
                        Event types
                    </th>
                ),
                customBodyRender: (value) => {
                    if (!isNotEmptyList(value)) {
                        return (
                            <Typography id="v2_grid_event_types" color="textSecondary">
                                —
                            </Typography>
                        );
                    }
                    return (
                        <div
                            id="v2_grid_event_types"
                            style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
                        >
                            {value.map(({ label, apiName }, idx) => (
                                <Tooltip key={`${apiName}_${idx}`} title={apiName || label}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <Chip
                                            id={`v2_grid_event_type_${idx}`}
                                            size="small"
                                            className={classes.chip}
                                            label={label}
                                        />
                                    </span>
                                </Tooltip>
                            ))}
                        </div>
                    );
                }
            }
        },
        {
            name: 'direction',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => handleSort('direction')}
                            key={'Direction'}
                        >
                            {'Direction'}
                            <TableSortLabel
                                className={classes.inline}
                                active={sortField === 'direction'}
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    return (
                        <Typography id={'direction'} key={'Direction'}>
                            {value}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'statusSummary',
            options: {
                sort: false,
                customHeadRender: () => (
                    <th className={classes.tableHeader} key="statusSummary">
                        Status
                    </th>
                ),
                customBodyRender: (value) => (
                    <Chip
                        id="v2_grid_status_summary"
                        size="small"
                        className={classes.chip}
                        label={value}
                    />
                )
            }
        },
        {
            name: 'agreementPeriod',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => handleSort('validFrom')}
                            key="agreementPeriod"
                        >
                            Start date → End date
                            <TableSortLabel
                                className={classes.inline}
                                active={sortField === 'validFrom'}
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => {
                    const { validFrom, validTo } = value || {};
                    const start = validFrom ? moment(validFrom).format('MM/DD/YYYY') : '—';
                    const end = validTo ? moment(validTo).format('MM/DD/YYYY') : '—';
                    return (
                        <Typography id="v2_grid_agreement_period" style={{ textAlign: 'left' }}>
                            {start} → {end}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'createdAt',
            options: {
                customHeadRender: () => {
                    return (
                        <th
                            className={classes.tableHeader}
                            onClick={() => handleSort('createdAt')}
                            key="createdAt"
                        >
                            Created At
                            <TableSortLabel
                                className={classes.inline}
                                active={sortField === 'createdAt'}
                                direction={sortDirection}
                            />
                        </th>
                    );
                },
                customBodyRender: (value) => (
                    <Typography id="v2_grid_created_at" style={{ textAlign: 'left' }}>
                        {value ? convertLocalDateTimeString(value.replace('Z', '')) : '—'}
                    </Typography>
                )
            }
        },
        {
            name: 'actions',
            options: {
                sort: false,
                customHeadRender: () => (
                    <th className={classes.tableHeader} key="actions">
                        Actions
                    </th>
                ),
                customBodyRender: (value) => {
                    const id = value?.id;
                    if (!id) {
                        return null;
                    }
                    return (
                        <>
                            <Tooltip title="Edit">
                                <IconButton
                                    id={`v2_grid_edit_${id}`}
                                    size="small"
                                    onClick={() => navigateToV2Agreement(id)}
                                    aria-label="Edit sharing agreement"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    );
                }
            }
        }
    ];

    const handleActivate = (selectedRows, id) => {
        let status = id !== 'activate';
        let selectedConnectionIds = [];
        selectedRows.data.map((row) => {
            if (data[row.dataIndex][STATUS_COLUMN_INDEX] === status) {
                selectedConnectionIds.push(data[row.dataIndex][0].id);
            }
            return true;
        });
        if (selectedConnectionIds.length > 0) {
            const connectionUpdate = {};
            connectionUpdate.connectionIDs = selectedConnectionIds;
            connectionUpdate.status = !status;
            updateVisibilityConnections(connectionUpdate, sessionValue);
        }
    };

    const options = {
        textLabels: {
            body: {
                noMatch: 'No record found'
            },
            selectedRows: {
                text: 'Selected'
            }
        },
        filter: false,
        download: false,
        filterType: 'dropdown',
        responsive: 'vertical',
        selectableRows:
            isV1Surface && !userRole.isSystemAdmin && isWriteAccessGranted
                ? 'single'
                : 'none',

        onCellClick: (cellData, cellMeta) => {
            if (isV2Surface) {
                return null;
            }
            const { colIndex } = cellMeta;
            const isSystemAdmin = sessionValue.userRole === SYSTEM_ADMIN;
            let cellConnId = cellData.props.children.props['date-connection-id'];
            if (colIndex === 0 || editToggle) {
                if (isV1Surface) {
                    const selectedConn = allConnections.find((row) => row.id === cellConnId);
                    if (!selectedConn) {
                        return null;
                    }
                    updateSession({ connectionId: cellConnId });
                    if (selectedConn.type === Constants.ACCOUNT_TYPE_VENDOR.toUpperCase()) {
                        const providerId =
                            selectedConn.provider_id ||
                            selectedConn.providerId ||
                            selectedConn.providerAccountId;
                        if (isBlank(providerId) || providerId === 'undefined') {
                            return null;
                        }
                        return history.push(
                            `/agreements/${Constants.ACCOUNT_TYPE_VENDOR.toUpperCase()}/${
                                providerId
                            }/edit${isSystemAdmin ? `?accountId=${selectedAccount.id}` : ''}`
                        );
                    } else {
                        return history.push(
                            `/connections/${cellConnId}/${selectedConn.name}/agreements${
                                isSystemAdmin ? `?accountId=${selectedAccount.id}` : ''
                            }`
                        );
                    }
                } else {
                    return isSystemAdmin
                        ? history.push(
                              `/provider-summary/v2/agreements/${cellConnId}?accountId=${selectedAccount.id}`
                          )
                        : history.push(`/provider-summary/v2/agreements/${cellConnId}`);
                }
            } else {
                return null;
            }
        },
        page: getCurrentPage() - 1,
        serverSide: true,
        count: getTotalElements(),
        sort: false,
        rowsPerPage: getPageSize(),
        print: false,
        search: false,
        viewColumns: false,
        expandableRows: false,
        rowsPerPageOptions: [5, 10, 20],
        onChangeRowsPerPage: (numberOfRows) => {
            let pageNo;
            if (getCurrentPage() === 1 || numberOfRows >= totalElements) {
                pageNo = 1;
            } else {
                let newPage;
                if (getPageSize() < numberOfRows) {
                    newPage = Math.ceil((getCurrentPage() * getPageSize()) / numberOfRows);
                    pageNo = newPage === 0 ? 1 : newPage;
                } else {
                    newPage = Math.ceil(((getCurrentPage() - 1) * getPageSize()) / numberOfRows);
                    pageNo = newPage === 0 ? 1 : newPage + 1;
                }
            }
            fetchGridData({
                page: pageNo,
                size: numberOfRows,
                sortOrder: sortOrder,
                sortField: sortField,
                searchTerm: searchValue
            });
        },
        onChangePage: (currentPage) => {
            fetchGridData({
                page: currentPage + 1,
                size: getPageSize(),
                sortOrder: sortOrder,
                sortField: sortField,
                searchTerm: searchValue
            });
        },
        customFooter: (count, page, rowsPerPage, changeRowsPerPage, onChangePage, textLabels) => {
            return (
                <CustomPagination
                    count={count}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    changeRowsPerPage={changeRowsPerPage}
                    onChangePage={onChangePage}
                    textLabels={textLabels}
                />
            );
        },

        customToolbar: () => {
            return (
                <React.Fragment>
                    <MuiThemeProvider theme={PRIMARY_BUTTON_LIGHT_THEME}>
                        {isV2Surface && filterProviderAccountId ? (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 8
                                }}
                            >
                                <Button
                                    id="backToProviders"
                                    onClick={() => {
                                        const backSearch =
                                            sessionValue.userRole === SYSTEM_ADMIN &&
                                            selectedAccount?.id
                                                ? `?accountId=${selectedAccount.id}`
                                                : '';
                                        history.push(`/connections/v2${backSearch}`);
                                    }}
                                    className={classes.addConnectionButton}
                                >
                                    BACK TO PROVIDERS
                                </Button>
                                <Button
                                    id="v2_refresh_sharing_agreements"
                                    onClick={() =>
                                        fetchGridData({
                                            page: getCurrentPage(),
                                            size: getPageSize(),
                                            sortOrder,
                                            sortField,
                                            searchTerm: searchValue
                                        })
                                    }
                                    className={classes.addConnectionButton}
                                >
                                    REFRESH
                                </Button>
                                {carrierConfigType && (
                                    <Button
                                        id="v2_carrier_config"
                                        onClick={navigateToCarrierConfig}
                                        className={classes.addConnectionButton}
                                    >
                                        CARRIER CONFIG
                                    </Button>
                                )}
                                {(userRole.isSystemAdmin ||
                                    userRole.isCarrierAdmin ||
                                    !userRole.isVisibilityProvider) && (
                                    <Button
                                        id="v2_add_agreement"
                                        variant="contained"
                                        size="small"
                                        style={{
                                            backgroundColor: '#0063a3',
                                            color: '#fff'
                                        }}
                                        onClick={navigateToCreateV2Agreement}
                                    >
                                        Add Agreement
                                    </Button>
                                )}
                            </div>
                        ) : showSearchBar ? (
                            <React.Fragment>
                                <MuiThemeProvider theme={SEARCH_BAR_DARK_THEME}>
                                    <TextField
                                        autoFocus
                                        value={searchValue}
                                        variant="standard"
                                        className={classes.searchBarDark}
                                        id="data-table-search-input"
                                        placeholder="Search by Provider Name"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    id={'closeIcon'}
                                                    onClick={() => clearSearch(true)}
                                                >
                                                    <CloseIcon
                                                        id={'closeIcon'}
                                                        className={classes.closeIconDark}
                                                    />
                                                </InputAdornment>
                                            )
                                        }}
                                        onChange={handleSearch}
                                    />
                                </MuiThemeProvider>
                            </React.Fragment>
                        ) : (
                            <Tooltip title={'Search'}>
                                <IconButton
                                    className={classes.iconButton}
                                    onClick={() => setShowSearchBar(true)}
                                    id={'data-table-search'}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {isV1Surface && !userRole.isSystemAdmin && (
                            <Button
                                id={'newConnection'}
                                onClick={clickHandle}
                                disabled={disableOtherOperation || !isWriteAccessGranted}
                                className={classes.addConnectionButton}
                            >
                                ADD CONNECTION
                            </Button>
                        )}
                    </MuiThemeProvider>
                </React.Fragment>
            );
        },

        customToolbarSelect: (selectedRows) =>
            isWriteAccessGranted ? (
                <div className={'custom-toolbar-select'}>
                    <Tooltip title={'Activate'}>
                        <IconButton
                            className={classes.iconButton}
                            id={'activate'}
                            onClick={() => handleActivate(selectedRows, 'activate')}
                        >
                            <ActivateIcon className={classes.deleteIcon} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={'Deactivate'}>
                        <IconButton
                            className={classes.iconButton}
                            id={'deactivate'}
                            onClick={() => handleActivate(selectedRows, 'deactivate')}
                        >
                            <DeactivateIcon className={classes.deleteIcon} />
                        </IconButton>
                    </Tooltip>
                </div>
            ) : null
    };

    return (
        <Auxiliary>
            <Paper className={!isAuthorized ? classes.vcPaper : classes.warningPaper}>
                <Auxiliary>
                    <MuiThemeProvider
                        theme={
                            !userRole.isSystemAdmin ? MUIDATATABLE_THEME : MUIDATATABLE_THEME_ALT
                        }
                    >
                        {valid && Object.entries(sessionValue).length > 0 ? (
                            <MUIDataTable
                                id={'vc_grid'}
                                title={
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                id={'pageTitle'}
                                                className={cs(classes.titleBar)}
                                            >
                                                {getGridTitle()}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }
                                data={data}
                                columns={getColumns()}
                                options={options}
                            />
                        ) : (
                            <Spinner />
                        )}
                    </MuiThemeProvider>
                </Auxiliary>
                {/*)}*/}
            </Paper>
            <AddConnection showModal={showModal} parentProps={props} handleClose={handleClose} />
        </Auxiliary>
    );
};

const mapStateToProps = (state) => ({
    allConnections: state.connection.connections,
    allSharingAgreementsV2: state.agreementV2.sharingAgreements,
    providerConfigV2: state.agreementV2.providerConfig,
    sortOrder: state.connection.sortOrder,
    sortField: state.connection.sortField,
    totalElements: state.connection.totalElements,
    totalV2Elements: state.agreementV2.totalElements,
    size: state.connection.pageSize,
    sizeV2: state.agreementV2.pageSize,
    page: state.connection.page,
    pageV2: state.agreementV2.page,
    valid: state.connection.valid,
    disableOtherOperation: state.agreement.disableOtherOperation,
    sidebarResource: state.header.sidebarResource,
    account: state.accounts.account,
    selectedAccount: state.accounts.selectedAccount,
    session: state.session.sessionValue
});

export default withRouter(
    connect(mapStateToProps, {
        getVisibilityConnections,
        setSortOrder,
        setSortField,
        resetAgreementList,
        updateVisibilityConnections,
        updateSession,
        getSharingAgreementV2: getSharingAgreementV2Action
    })(withStyles(VisibilityConnectionStyles)(VisibilityConnectionsGridBase))
);
